import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-final-form';
import { connect } from 'react-redux';

import { getContentLocale } from '~src/services/session';
import { ContentLocalesI } from '~src/interfaces/session.types';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { Topic } from '~src/interfaces/topic.interface';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import {
  addTopicQuestion,
  deleteTopicQuestion,
  editTopicQuestion,
  QuestionSubmit,
} from '~src/modules/topics/actions/topicActions';
import { StoreI } from '~src/rootReducer';
import { Question } from '~src/interfaces/question.interface';
import { getTopics } from '~src/modules/topics/reducers/topicsReducer';
import SectionHeader from '~src/modules/surveys/components/topic_select/headers/SectionHeader';
import imgLibrary from '~src/images/question_example_library.png';
import imgCustom from '~src/images/question_example_custom.png';
import { Survey } from '~src/modules/surveys/interfaces';

import CustomQuestions from './CustomQuestions';
import TopicQuestions from './TopicQuestions';
import { EmptyMessage, SpinnerContainer } from './topicQuestionsForm.sc';
import { useTranslation } from 'react-i18next';

interface StateProps {
  topics: Topic[];
  contentLocale: string;
}

interface DispatchProps {
  addTopicQuestion: (topicUuid: string, value: QuestionSubmit) => ApiReduxResp<Question>;
  editTopicQuestion: (questionId: string, payload: QuestionSubmit) => ApiReduxResp<Question>;
  deleteTopicQuestion: (
    topicId: string,
    questionId: string,
    name: ContentLocalesI | null,
  ) => ApiReduxResp<void>;
}

interface OwnProps {
  survey: Survey;
  loadingInit: boolean;
  isPrivate?: boolean;
  selectedQuestions: Record<string, Record<string, boolean>>;
  mandatoryQuestions: string[];
  changeShowSave: (value: boolean) => void;
  defaultContentLocale: string;
  createdTopic: string | null;
  deleteField: (field: string) => void;
}

type Props = StateProps & DispatchProps & OwnProps;

const TopicQuestionsFormInner: FC<Props> = (props) => {
  const [customTopic, setCustomTopic] = useState<undefined | Topic>(undefined);
  const [libraryTopics, setLibraryTopics] = useState<Topic[]>([]);
  const [splitLoading, setSplitLoading] = useState(true);
  const [t] = useTranslation();

  const form = useForm();

  useEffect(() => {
    if (props.isPrivate) {
      setSplitLoading(true);
      const tmpLibraryTopics = [...libraryTopics];
      props.topics.forEach((topic) => {
        if (topic.private && String(topic.subject) === String(props.survey.subject)) {
          setCustomTopic(topic);
        } else {
          tmpLibraryTopics.push(topic);
        }
      });
      setLibraryTopics(tmpLibraryTopics);
    } else {
      setLibraryTopics(props.topics);
    }
    setSplitLoading(false);
  }, [Object.keys(props.selectedQuestions).length]);

  function selectAll(topic: Topic, value: boolean): Promise<void> {
    return new Promise((res, rej) => {
      if (topic.questions) {
        topic.questions.forEach((question) => {
          form.change(`questions.${topic.id}.${question.id}`, value);
        });
        props.changeShowSave(true);
        res();
      } else {
        rej();
      }
    });
  }

  function editCustomQuestion(id: string, val: QuestionSubmit) {
    return props.editTopicQuestion(id, val).then((res) => {
      if (!res.error && customTopic) {
        const oldState = { ...customTopic };
        if (oldState.questions) {
          oldState.questions = oldState.questions.map((question) =>
            question.id === id ? { ...question, ...val } : question,
          );

          setCustomTopic(oldState);
        }
      }
      return res;
    });
  }

  return (
    <>
      {!props.loadingInit && (props.topics.length <= 0 || !props.selectedQuestions) ? (
        <EmptyMessage data-translation="x.surveys.topics.emptymessage">
          {t('x.surveys.topics.emptymessage')}
        </EmptyMessage>
      ) : (
        <>
          {props.isPrivate && (customTopic || splitLoading) && (
            <>
              <SectionHeader
                title={{
                  key: 'x.survey_details.header.custom_questions',
                  txt: t('x.survey_details.header.custom_questions'),
                }}
                tooltipText={{
                  key: 'x.survey_details.header.custom_questions',
                  txt: t('x.survey_details.header.custom_questions'),
                }}
                pictureText={{
                  key: 'x.survey_details.header.custom_questions.picture',
                  txt: t('x.survey_details.header.custom_questions.picture'),
                }}
                imageUrl={imgCustom}
              />
              {props.loadingInit || splitLoading ? (
                <SpinnerContainer>
                  <LoadingSpinner />
                </SpinnerContainer>
              ) : (
                customTopic &&
                props.selectedQuestions[customTopic.id] && (
                  <CustomQuestions
                    key={customTopic.id}
                    selectedQuestions={props.selectedQuestions[customTopic.id]}
                    mandatoryQuestions={props.mandatoryQuestions}
                    selectAll={selectAll}
                    topic={customTopic}
                    changeShowSave={props.changeShowSave}
                    defaultContentLocale={props.defaultContentLocale}
                    addQuestion={props.addTopicQuestion}
                    editQuestion={editCustomQuestion}
                    deleteQuestion={props.deleteTopicQuestion}
                    deleteField={props.deleteField}
                    contentLocale={props.contentLocale}
                  />
                )
              )}
            </>
          )}
          <>
            {props.isPrivate && (
              <SectionHeader
                title={{
                  key: 'x.survey_details.header.library',
                  txt: t('x.survey_details.header.library'),
                }}
                tooltipText={{
                  key: 'x.survey_details.header.library',
                  txt: t('x.survey_details.header.library'),
                }}
                pictureText={{
                  key: 'x.survey_details.header.library.picture',
                  txt: t('x.survey_details.header.library.picture'),
                }}
                imageUrl={imgLibrary}
              />
            )}
            {props.loadingInit || splitLoading ? (
              <SpinnerContainer>
                <LoadingSpinner />
              </SpinnerContainer>
            ) : (
              libraryTopics.map((topic) => {
                return (
                  !topic.private && (
                    <TopicQuestions
                      changeShowSave={props.changeShowSave}
                      key={topic.id}
                      topic={topic}
                      selectAll={selectAll}
                      selectedQuestions={props.selectedQuestions[topic.id]}
                      mandatoryQuestions={props.mandatoryQuestions}
                      addQuestion={props.addTopicQuestion}
                      defaultContentLocale={props.defaultContentLocale}
                      change={form.change}
                      isCreated={topic.id === props.createdTopic}
                      isPrivate={props.isPrivate}
                    />
                  )
                );
              })
            )}
          </>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: StoreI, ownProps: OwnProps): StateProps => ({
  // LN-1024 show unarchived topics and also archived ones where we have selected at least one question
  topics: getTopics(state).filter(t => !t.archived || (ownProps.selectedQuestions[t.id] && Object.values(ownProps.selectedQuestions[t.id]).some(x => x))),
  contentLocale: getContentLocale(state),
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  addTopicQuestion: (topicUuid, value) => dispatch(addTopicQuestion(topicUuid, value)),
  editTopicQuestion: (topicUuid, value) => dispatch(editTopicQuestion(topicUuid, value)),
  deleteTopicQuestion: (topicUuid, questionId, name) =>
    dispatch(deleteTopicQuestion(topicUuid, questionId, name)),
});

const TopicQuestionsFormInnerSuper = connect(mapStateToProps, mapDispatchToProps)(TopicQuestionsFormInner);
export default TopicQuestionsFormInnerSuper;
